import React, { useEffect, useRef, useContext, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Markup from '../../markup';
import FileImageUploader from '../../builder/utils/FileImageUploader';
import BuilderContext from '../../builder/hooks/context';
import { trackImageUpload, trackRemovedUploadedImage } from '../../utils/Analytics';

export const BaseImageFile = ({ image, setImage, name = '', hasPreview = false, loading = false, handleRemoveImage = null, id = '', reorderedImages = [], side = 'front' }) => {
  const el = useRef(null);
  const [loadingError, setLoadingError] = useState(false);
  const [isCentered, setIsCentered] = useState(false);
  const [updateSide, setUpdateSide] = useState('');
  const { builder } = useContext(BuilderContext);

  useEffect(() => {
    if (side) {
      setUpdateSide(side)
    }
  }, [side]);

  useEffect(() => {
    const uploader = new FileImageUploader({
      fileinput: el.current,
      onLoad: (image) => {
        builder?.imagesData?.setActiveUploadsCounter((prevCount) => prevCount + 1);
        setImage(image, null);
      },
      onUpload: (public_url) => {
        builder?.imagesData?.setActiveUploadsCounter((prevCount) => prevCount - 1);
        setImage(null, public_url);
        trackImageUpload(side);
      },
      onError: () => {
        builder?.imagesData?.setActiveUploadsCounter((prevCount) => prevCount - 1);
        setImage(null);
        setLoadingError(true);
        setTimeout(() => setLoadingError(false), 5000);
      }
    });

    builder.setOrdering(reorderedImages); 

    if (el.current) return uploader.destroy(el.current);
  }, [el, reorderedImages]);

  useEffect(() => {
    if (loadingError && loading) setImage(null);
  }, [loadingError, loading]);

  const getDefaultPositions = (productId, activeSide) => {
    const tshirtPositions = {
      image_front: { x: 365, y: 1370 },
      image_back: { x: 1298, y: 1370 },
      image_left: { x: 1635, y: 425 },
      image_right: { x: 1135, y: 425 },
    };
  
    const hoodiePositions = {
      image_front: { x: 885, y: 1480 },
      image_back: { x: 230, y: 1520 },
      image_left: { x: 385, y: 460 },
      image_right: { x: 915, y: 460 },
    };
  
    const positions = productId === '1206225' ? tshirtPositions : productId === '1207887' ? hoodiePositions : null;
  
    return positions[`image_${activeSide}`] || { x: 0, y: 0 };
  }

  const handleCenterClick = (id) => {
    if (updateSide) {
      const activeSide = Object.keys(builder.imagesData.sponsorImages).find(side => side === updateSide && builder.imagesData.sponsorImages[side]);
      if (activeSide && builder.imagesData.sponsorImages[activeSide]) {
        setIsCentered(!isCentered);
  
        const productId = builder?.manifest?.productId;
        
        const position = getDefaultPositions(productId, activeSide);
  
        if (position) {
          const layer = builder.layersData.find(layer => layer.id === id);
  
          if (layer) {
            layer.layer.position = position;
            builder?.setAnimationState(true);
            builder?.setCamera(side);
            layer.layer.needsRender = true;
          }
        }
      }
    }
  };

  return (
    <>
      {loadingError && <Markup.Global.Notification.ErrorNotification>An error occured while loading the image</Markup.Global.Notification.ErrorNotification>}
      {hasPreview ? (
        <Markup.Global.ImageFile.BaseItem loading={loading?.toString()} image={image?.src} name={image?.alt || name}>
          {image && (
            <>
              <Markup.Global.ImageFile.BaseFileDrag/>
              {builder?.manifest?.productId === '1206225' || builder?.manifest?.productId === '1207887' ? <Markup.Global.ImageFile.BaseFileCentered onClick={() => handleCenterClick(id)} /> : null }
              <Markup.Global.ImageFile.BaseFileRemove onClick={() => handleRemoveImage(id)} />
            </>
          )}
          <Markup.Global.ImageFile.BaseFileInput accept={FileImageUploader.allowedTypes.join(',')} ref={el} />
        </Markup.Global.ImageFile.BaseItem>
      ) : (
        <Markup.Global.ImageFile.InlineFile active={image !== null}>
          <>{image?.alt || name}</>
          <Markup.Global.ImageFile.BaseFileInput disabled={loading} accept={FileImageUploader.allowedTypes.join(',')} ref={el} />
          <Markup.Global.ImageFile.BaseFileDrag />
          {builder?.manifest?.productId === '1206225' || builder?.manifest?.productId === '1207887' ? <Markup.Global.ImageFile.BaseFileCentered onClick={() => handleCenterClick(id)} /> : null}
          {!image && <Markup.Global.ImageFile.InlineFileLoadIco />}
          {image && <Markup.Global.ImageFile.InlineFileRemoveIco loading={loading?.toString()} onClick={() => setImage(null)} />}
        </Markup.Global.ImageFile.InlineFile>
      )}
    </>
  );
};

export const BaseContainer = ({ images, setImage, side, minimal, hasPreview = true, isSleeves = false }) => {
  const { builder } = useContext(BuilderContext);
  let reorderedImages = [...images];

  const actualSide = isSleeves ? `${side}_long` : side;

  useEffect(() => {
    builder.refreshLayers();
  }, [reorderedImages, builder]);

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
  
    const [reorderedItem] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, reorderedItem);
  
    builder.imagesData.sponsorImages[actualSide] = reorderedImages;
    builder.setOrdering(reorderedImages)
  
    setImage(null, result.destination.index, reorderedItem.public_url);
  };

  const handleRemoveImage = (id) => {
    const updatedImages = reorderedImages.map((image, index) => {
      if (image && image.id === id) {
        reorderedImages[index] = null;
      }
      return image;
    });

    reorderedImages = updatedImages;
  
    builder.imagesData.sponsorImages[side] = reorderedImages;
    const indexToRemove = reorderedImages.findIndex((image) => image?.id === id);
    builder.setOrdering(reorderedImages)

    setImage(null, indexToRemove);
    trackRemovedUploadedImage(side);
  };

  return (
    <>
      {reorderedImages.length > 0 ? (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="images">
            {(provided) => (
              <Markup.Global.ImageFile.BaseContainer minimal={minimal} {...provided.droppableProps} ref={provided.innerRef}>
                {reorderedImages.map((image, i) => (
                  <Draggable key={side + i} draggableId={side + i} index={i}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <BaseImageFile
                          hasPreview={hasPreview}
                          loading={image?.loading}
                          image={image?.image}
                          id={image?.id}
                          setImage={(image, public_url) => setImage(image, i, public_url)}
                          handleRemoveImage={handleRemoveImage}
                          reorderedImages={reorderedImages}
                          side={side}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Markup.Global.ImageFile.BaseContainer>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
       null
      )}
    </>
  );
};
